import { loadPage } from '@/helpers';
import route from '@/lib/route';
import { selectShouldShowAppOnboarding } from '@/redux/selectors/users';
import store from '@/redux/store';

import { getCookie, removeCookie, setCookie } from './cookie';

export const POST_AUTH_DESTINATION_COOKIE = 'zlPostAuthDestination';

const POST_AUTH_DESTINATION_EXPIRY_DAYS = 1;

const AUTH_PATH_PREFIXES = ['/login', '/signup', '/socialLogin', '/passwordReset', '/emailVerification'];

const EXCLUDED_PATH_PREFIXES = [
  ...AUTH_PATH_PREFIXES,
  // Default landing / fallback destinations. Returning a user "back" to these is meaningless
  // (goToPostAuthDestination already falls back to `/`), and — more importantly — guard redirects
  // bounce unauthenticated users here (e.g. a private page -> `/home`). Excluding them prevents
  // such a bounce from overwriting the page the user actually intended to reach.
  '/',
  '/home',
  '/highlights',
  '/map',
  '/welcome',
  '/checkout-completed',
  '/checkout-cancelled',
  '/partnerDonation',
  '/admin',
  '/redirect',
  '/start',
  '/embed',
  '/mapView',
  '/album',
];

const normalizePathname = (pathname: string) => {
  if (!pathname.startsWith('/')) {
    return `/${pathname}`;
  }
  return pathname;
};

const getTrustedHosts = (): Set<string> => {
  const hosts = new Set<string>();

  const cookieDomain = process.env.PREACT_APP_COOKIE_DOMAIN;
  if (cookieDomain) {
    const bare = cookieDomain.startsWith('.') ? cookieDomain.slice(1) : cookieDomain;
    hosts.add(bare);
  }

  const educatorsAppURL = store.getState().remoteConfig?.educatorsAppURL;
  if (educatorsAppURL) {
    try {
      hosts.add(new URL(educatorsAppURL).host);
    } catch {
      // ignore invalid remote config URL
    }
  }

  const extraDomains = process.env.PREACT_APP_TRUSTED_REDIRECT_DOMAINS;
  if (extraDomains) {
    extraDomains.split(',').forEach((domain) => {
      const trimmed = domain.trim();
      if (trimmed) {
        hosts.add(trimmed);
      }
    });
  }

  return hosts;
};

const hostMatchesTrustedDomain = (host: string, trustedHost: string) => {
  return host === trustedHost || host.endsWith(`.${trustedHost}`);
};

// Native-app deep-link schemes (e.g. zoolife://, zoolife-dev://) used to hand off back to the
// mobile app after auth. Matches `zoolife` optionally followed by a scheme-safe suffix, then `://`.
const NATIVE_APP_SCHEME_REGEX = /^zoolife[a-z0-9.+-]*:\/\//i;

export const isTrustedRedirect = (value: string | undefined | null): value is string => {
  if (!value) {
    return false;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }

  if (trimmed.startsWith('/') && !trimmed.startsWith('//')) {
    return true;
  }

  if (NATIVE_APP_SCHEME_REGEX.test(trimmed)) {
    return true;
  }

  try {
    const url = new URL(trimmed);
    const trustedHosts = getTrustedHosts();
    return Array.from(trustedHosts).some((trustedHost) => hostMatchesTrustedDomain(url.host, trustedHost));
  } catch {
    return false;
  }
};

export const shouldSaveRedirectForPath = (pathname: string) => {
  const normalized = normalizePathname(pathname.split('?')[0] ?? pathname);
  return !EXCLUDED_PATH_PREFIXES.some((prefix) => normalized === prefix || normalized.startsWith(`${prefix}/`));
};

export const savePostAuthDestination = (value: string) => {
  if (!isTrustedRedirect(value)) {
    return;
  }
  setCookie(POST_AUTH_DESTINATION_COOKIE, value, { expires: POST_AUTH_DESTINATION_EXPIRY_DAYS });
};

export const getPostAuthDestination = () => {
  return getCookie(POST_AUTH_DESTINATION_COOKIE) ?? null;
};

/** True when the saved destination targets `path` (exact or as a path prefix), ignoring query string. */
export const isPostAuthDestination = (path: string) => {
  const destination = getPostAuthDestination();
  if (!destination) {
    return false;
  }
  const pathname = destination.split('?')[0];
  return pathname === path || pathname.startsWith(`${path}/`);
};

export const clearPostAuthDestination = () => {
  removeCookie(POST_AUTH_DESTINATION_COOKIE);
};

const mapPathToPostAuthDestination = (pathname: string, search: string) => {
  if (pathname === '/talk-schedule') {
    const destination = new URL('/schedule', window.location.origin);
    const searchParams = new URLSearchParams(search);
    searchParams.delete('ml');
    searchParams.forEach((value, key) => {
      destination.searchParams.set(key, value);
    });
    return `${destination.pathname}${destination.search}`;
  }

  return `${pathname}${search}`;
};

/** Merge the navigation url's query over the current location's query (url wins on conflicts). */
const mergeNavigationSearch = (urlSearch: string, ignoreQueryParams?: string[]) => {
  const merged = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  new URLSearchParams(urlSearch).forEach((value, key) => merged.set(key, value));
  ignoreQueryParams?.forEach((param) => merged.delete(param));
  const serialized = merged.toString();
  return { search: serialized ? `?${serialized}` : '', params: merged };
};

/** Single entry point for anonymous navigation tracking. */
export const syncPostAuthDestinationForNavigation = (
  url: string,
  { logged, sessionChecked, ignoreQueryParams }: { logged: boolean; sessionChecked: boolean; ignoreQueryParams?: string[] },
) => {
  if (!sessionChecked || logged || typeof window === 'undefined') {
    return;
  }

  const [pathname, urlSearch = ''] = url.split('?');
  const { search, params } = mergeNavigationSearch(urlSearch, ignoreQueryParams);

  const redirectToParam = params.get('redirect');
  if (redirectToParam) {
    savePostAuthDestination(redirectToParam);
    return;
  }

  const normalizedPathname = pathname || url;
  if (!shouldSaveRedirectForPath(normalizedPathname)) {
    return;
  }

  savePostAuthDestination(mapPathToPostAuthDestination(normalizedPathname, search));
};

export const goToPostAuthDestination = () => {
  const destination = getPostAuthDestination();
  clearPostAuthDestination();

  if (destination && isTrustedRedirect(destination)) {
    // Absolute targets — external http(s) URLs and native-app deep links (zoolife://) — need a
    // full navigation so the browser / OS handles them. Only in-app relative paths use the router.
    if (!destination.startsWith('/')) {
      loadPage(destination);
      return;
    }
    route(destination, true);
    return;
  }

  route('/', true);
};

export const routeToNextAuthStep = (options?: { emailVerificationSearch?: string }) => {
  const state = store.getState();
  const { logged, isRealEmail } = state.user;

  if (!logged) {
    return;
  }

  if (!isRealEmail) {
    const search = options?.emailVerificationSearch;
    route(search ? `/emailVerification?${search}` : '/emailVerification', true);
    return;
  }

  if (selectShouldShowAppOnboarding(state)) {
    route('/home', true);
    return;
  }

  goToPostAuthDestination();
};
